<script setup>
// SiteUp: aba "Follow-ups" do painel do Copiloto — visão somente-leitura dos
// follow-ups da IA: agendados (tool schedule_follow_up), em andamento (slots do
// follow-up automático) e os próximos da fila do automático. Strings em pt-BR
// hardcoded, mesmo padrão do Painel da IA (ai_panel/Index.vue).
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import AiAgentMetricsAPI from 'dashboard/api/aiAgentMetrics';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { useUISettings } from 'dashboard/composables/useUISettings';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';

const route = useRoute();
const { updateUISettings } = useUISettings();

const data = ref(null);
const isFetching = ref(false);
const loadError = ref(null); // 'forbidden' | 'generic' | null

const fetchFollowUps = async () => {
  isFetching.value = true;
  loadError.value = null;
  try {
    const response = await AiAgentMetricsAPI.followUps();
    data.value = response.data;
  } catch (error) {
    const status = error?.response?.status;
    loadError.value = status === 401 || status === 403 ? 'forbidden' : 'generic';
  } finally {
    isFetching.value = false;
  }
};

const headerButtons = computed(() => [
  {
    key: 'refresh',
    icon: 'i-ri-restart-line',
    tooltip: 'Atualizar',
  },
]);

const handleHeaderAction = action => {
  if (action === 'refresh') fetchFollowUps();
};

// Mesmo comportamento de fechar do Copilot.vue (X do header).
const closePanel = () => {
  updateUISettings({
    is_copilot_panel_open: false,
    is_contact_sidebar_open: false,
  });
};

const conversationPath = displayId =>
  frontendURL(
    conversationUrl({ accountId: route.params.accountId, id: displayId })
  );

const formatTime = iso => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

// Linha de horário por categoria (cada uma tem um campo de tempo diferente).
const itemTime = (sectionKey, item) => {
  if (sectionKey === 'scheduled') return `para ${formatTime(item.follow_up_at)}`;
  if (sectionKey === 'active') return `enviado ${formatTime(item.sent_at)}`;
  return `previsto ${formatTime(item.eta)}`;
};

const sections = computed(() => [
  {
    key: 'scheduled',
    title: 'Agendados pela IA',
    icon: 'i-ri-calendar-schedule-line',
    empty: 'Nenhum follow-up agendado pela IA.',
    items: data.value?.scheduled || [],
  },
  {
    key: 'active',
    title: 'Em andamento',
    icon: 'i-ri-hourglass-line',
    empty: 'Nenhum follow-up automático aguardando resposta.',
    items: data.value?.active || [],
  },
  {
    key: 'queue',
    title: 'Próximos da fila',
    icon: 'i-ri-list-ordered',
    empty: 'Ninguém na fila do follow-up automático.',
    items: data.value?.queue || [],
  },
]);

onMounted(fetchFollowUps);
</script>

<template>
  <div class="flex flex-col h-full min-h-0 w-full text-sm">
    <SidebarActionsHeader
      title="Follow-ups da IA"
      :buttons="headerButtons"
      @click="handleHeaderAction"
      @close="closePanel"
    />

    <div class="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-5">
      <div
        v-if="loadError === 'forbidden'"
        class="flex items-start gap-2 px-3 py-2 rounded-xl bg-n-amber-2 text-n-amber-11 text-xs"
      >
        <span class="i-ri-lock-line size-4 shrink-0 mt-0.5" />
        <span>
          A lista de follow-ups é restrita a administradores da conta.
        </span>
      </div>

      <div
        v-else-if="loadError === 'generic'"
        class="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-n-slate-2 text-n-slate-11 text-xs"
      >
        <span>Não foi possível carregar os follow-ups.</span>
        <button
          type="button"
          class="shrink-0 rounded-md px-2.5 py-1 text-xs font-medium bg-n-teal-9/15 text-n-teal-11"
          @click="fetchFollowUps"
        >
          Tentar de novo
        </button>
      </div>

      <p v-else-if="isFetching && !data" class="text-xs text-n-slate-10">
        Carregando follow-ups…
      </p>

      <template v-else>
        <section
          v-for="section in sections"
          :key="section.key"
          class="space-y-2"
        >
          <h4
            class="flex items-center gap-1.5 text-xs font-medium text-n-slate-12"
          >
            <span :class="section.icon" class="size-3.5 text-n-slate-10" />
            {{ section.title }}
            <span class="font-normal text-n-slate-10">
              ({{ section.items.length }})
            </span>
          </h4>

          <p v-if="!section.items.length" class="text-xs text-n-slate-10">
            {{ section.empty }}
          </p>

          <router-link
            v-for="item in section.items"
            :key="`${section.key}-${item.conversation_display_id}`"
            :to="conversationPath(item.conversation_display_id)"
            class="block rounded-xl border border-n-weak px-3 py-2 hover:bg-n-alpha-1 transition-colors"
          >
            <div class="flex items-center justify-between gap-2">
              <span class="text-sm text-n-slate-12 truncate">
                {{ item.contact_name || 'Contato sem nome' }}
              </span>
              <span class="text-[10px] text-n-slate-10 shrink-0">
                #{{ item.conversation_display_id }}
              </span>
            </div>
            <p class="text-xs text-n-slate-11 mt-0.5">
              {{ itemTime(section.key, item)
              }}<template v-if="item.inbox_name"> · {{ item.inbox_name }}</template>
            </p>
            <p
              v-if="item.note"
              class="text-xs text-n-slate-10 mt-0.5 italic line-clamp-2"
            >
              “{{ item.note }}”
            </p>
          </router-link>
        </section>
      </template>
    </div>
  </div>
</template>
